import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { ApiClient } from '../api/client';
import ErrorBanner from './ErrorBanner';
import { StageTabs } from './AppDetailComponents';
import { AppDetailAutomationSettings } from './AppDetailAutomationSettings';
import type {
  AppDetail,
  AppItem,
  AppStage,
  AppStageSecret,
  AppStageSecretsResponse,
  DeployKeyStatus,
  IdentityHealthResponse,
  IdentityServiceSummary,
  OpsOverview,
  Server,
  WorkerNode
} from '../types';

type StageTab = {
  id: string;
  label: string;
  stage?: AppStage | null;
};

type StageSnapshot = {
  id: string;
  name: string;
  isDefault: boolean;
  slugSuffix: string;
  dnsRecord: string | null;
  containers: string[];
  gitlabEnvironmentName: string | null;
  stage: AppStage | null;
};

type SecretScopeSelection = {
  scope: 'general' | 'stage';
  stageId?: string;
};

function isWorkerTarget(server: Server): boolean {
  return server.capabilities?.transport === 'worker';
}

function workerTargetTags(server: Server): string[] {
  const tags = server.capabilities?.worker_tags;
  return Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === 'string') : [];
}

function serverOptionLabel(server: Server): string {
  if (isWorkerTarget(server)) {
    const tags = workerTargetTags(server);
    return `${server.name} (${tags.length ? `worker: ${tags.join(', ')}` : 'worker'})`;
  }
  return `${server.name} (${server.host})`;
}

function workerTagOptions(nodes: WorkerNode[]): Array<{ tag: string; label: string }> {
  const counts = new Map<string, { online: number; total: number }>();
  for (const node of nodes) {
    for (const tag of node.tags ?? []) {
      const trimmed = tag.trim();
      if (!trimmed) {
        continue;
      }
      const current = counts.get(trimmed) ?? { online: 0, total: 0 };
      current.total += 1;
      if (node.status.toLowerCase() === 'online' || node.status.toLowerCase() === 'busy') {
        current.online += 1;
      }
      counts.set(trimmed, current);
    }
  }
  return Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([tag, count]) => ({
      tag,
      label: `${tag} (${count.online}/${count.total} online)`
    }));
}

export function AppDetailSettingsView({
  apiClient,
  appId,
  app,
  detail,
  stages,
  servers,
  workerNodes,
  dnsDomains,
  dnsDefault,
  settingsScopeTabs,
  selectedSettingsStageId,
  selectedContainer,
  hasContainers,
  deployKeyStatus,
  deployKeyBusy,
  gitlabCiBusy,
  selfBusy,
  templateBusy,
  infraHelperBusy,
  infraCatalogBusy,
  settingsScopeIsGeneral,
  settingsStageSnapshot,
  settingsSecretScope,
  settingsSecretScopeLabel,
  additionalStages,
  opsOverview,
  opsResult,
  opsBusyKey,
  stageBusy,
  stageResult,
  stageError,
  targetBusy,
  targetResult,
  targetError,
  stageSecrets,
  availableApps,
  stageSecretsBusy,
  stageSecretsResult,
  manifestSecrets,
  availableManifestSecrets,
  effectiveNewSecretKey,
  selectedNewSecretSpec,
  selectedNewSecretType,
  selectedManifestFilePath,
  newSecretDescription,
  newSecretValue,
  newSecretSourceType,
  newExternalAppId,
  newExternalStageMode,
  newExternalStageId,
  newExternalSecretKey,
  editingSecretId,
  editSecretValue,
  editSecretSourceType,
  editExternalAppId,
  editExternalStageMode,
  editExternalStageId,
  editExternalSecretKey,
  unifiedPreviewBusy,
  hookUrl,
  hookStatus,
  hookInstalled,
  hookDisabled,
  hookIncomplete,
  hookNeedsRecreate,
  hookBusy,
  autofixEnabled,
  autofixFeatureBranches,
  autofixPrompt,
  autofixResult,
  appAutopilotMode,
  appEnablePushReminder,
  appTemplateBuildFailed,
  appTemplateMergeConflict,
  appTemplateReadySemi,
  appTemplateReadyFull,
  appAutopilotResult,
  pipelineWaitAllowed,
  pipelineWaitBusy,
  pipelineWaitResult,
  autoMrFeedback,
  mrFeedbackResult,
  restartResult,
  selfResult,
  templateResult,
  infraHelperResult,
  backgroundJobResult,
  onSelectSettingsScope,
  onOpenStageCreateDialog,
  onRefreshStages,
  onUpdateAppKey,
  onEnsureDeployKey,
  onUpdateSelfApp,
  onUpdateTemplateApp,
  onUpdateInfrastructureHelper,
  onApproveInfrastructureCommandCatalog,
  onDenyInfrastructureCommandCatalog,
  onLoadGitlabCiPreview,
  onDeleteStage,
  onEnsureStageDomain,
  onUpdateStageSettings,
  onUpdateAppTargets,
  onUseWorkerTagForConversations,
  onRotateSecret,
  onDeleteStageSecret,
  onUpdateStageSecret,
  onCreateStageSecret,
  onMoveStageSecret,
  onSetEditingSecretId,
  onSetEditSecretValue,
  onSetEditSecretSourceType,
  onSetEditExternalAppId,
  onSetEditExternalStageMode,
  onSetEditExternalStageId,
  onSetEditExternalSecretKey,
  onSetNewSecretKey,
  onSetNewSecretType,
  onSetNewSecretDescription,
  onSetNewSecretValue,
  onSetNewSecretSourceType,
  onSetNewExternalAppId,
  onSetNewExternalStageMode,
  onSetNewExternalStageId,
  onSetNewExternalSecretKey,
  onLoadUnifiedPreview,
  onSetAutofixEnabled,
  onSetAutofixFeatureBranches,
  onSetAutofixPrompt,
  onSaveAutofix,
  onResetAutofixTemplate,
  onSetAppAutopilotMode,
  onSetAppEnablePushReminder,
  onSetAppTemplateBuildFailed,
  onSetAppTemplateMergeConflict,
  onSetAppTemplateReadySemi,
  onSetAppTemplateReadyFull,
  onSaveAppAutopilot,
  onUpdatePipelineWaitAllowed,
  onSetAutoMrFeedback,
  onSaveMrFeedback,
  onAddGitlabHook,
  onRemoveGitlabHook,
  onSetSelectedContainer,
  onRestartContainers,
  onSetBackgroundJobResult,
  onSetError,
  onDismissStageError,
  onDismissTargetError,
  identityServices,
  identityServicesBusy,
  identityResult,
  ymlIdConfigured,
  identityHealth,
  identityHealthBusy,
  onReconcileIdentity,
  onDeprovisionIdentity,
  onManualIdentitySecrets,
  onCheckIdentityHealth
}: {
  apiClient: ApiClient;
  appId: string;
  app: AppItem;
  detail: AppDetail;
  stages: AppStage[];
  servers: Server[];
  workerNodes: WorkerNode[];
  dnsDomains: string[];
  dnsDefault: string | null;
  settingsScopeTabs: StageTab[];
  selectedSettingsStageId: string;
  selectedContainer: string;
  hasContainers: boolean;
  deployKeyStatus: DeployKeyStatus | null;
  deployKeyBusy: boolean;
  gitlabCiBusy: boolean;
  selfBusy: boolean;
  templateBusy: boolean;
  infraHelperBusy: boolean;
  infraCatalogBusy: boolean;
  settingsScopeIsGeneral: boolean;
  settingsStageSnapshot: StageSnapshot;
  settingsSecretScope: SecretScopeSelection;
  settingsSecretScopeLabel: string;
  additionalStages: AppStage[];
  opsOverview: OpsOverview | null;
  opsResult: string | null;
  opsBusyKey: string | null;
  stageBusy: boolean;
  stageResult: string | null;
  stageError: string | null;
  targetBusy: boolean;
  targetResult: string | null;
  targetError: string | null;
  stageSecrets: AppStageSecret[];
  availableApps: AppItem[];
  stageSecretsBusy: boolean;
  stageSecretsResult: string | null;
  manifestSecrets: OpsOverview['secrets'];
  availableManifestSecrets: OpsOverview['secrets'];
  effectiveNewSecretKey: string;
  selectedNewSecretSpec: OpsOverview['secrets'][number] | null;
  selectedNewSecretType: 'env' | 'file';
  selectedManifestFilePath: string;
  newSecretDescription: string;
  newSecretValue: string;
  newSecretSourceType: 'manual' | 'external';
  newExternalAppId: string;
  newExternalStageMode: 'fixed' | 'production' | 'same_stage';
  newExternalStageId: string;
  newExternalSecretKey: string;
  editingSecretId: string | null;
  editSecretValue: string;
  editSecretSourceType: 'manual' | 'external';
  editExternalAppId: string;
  editExternalStageMode: 'fixed' | 'production' | 'same_stage';
  editExternalStageId: string;
  editExternalSecretKey: string;
  unifiedPreviewBusy: boolean;
  hookUrl: string | null;
  hookStatus: import('../types').HookStatus | null;
  hookInstalled: boolean | null;
  hookDisabled: boolean;
  hookIncomplete: boolean;
  hookNeedsRecreate: boolean;
  hookBusy: boolean;
  autofixEnabled: boolean;
  autofixFeatureBranches: boolean;
  autofixPrompt: string;
  autofixResult: string | null;
  appAutopilotMode: import('../types').AutopilotMode;
  appEnablePushReminder: boolean;
  appTemplateBuildFailed: string;
  appTemplateMergeConflict: string;
  appTemplateReadySemi: string;
  appTemplateReadyFull: string;
  appAutopilotResult: string | null;
  pipelineWaitAllowed: boolean;
  pipelineWaitBusy: boolean;
  pipelineWaitResult: string | null;
  autoMrFeedback: boolean;
  mrFeedbackResult: string | null;
  restartResult: string | null;
  selfResult: string | null;
  templateResult: string | null;
  infraHelperResult: string | null;
  backgroundJobResult: { message: string; job?: import('./BackgroundJobButton').JobState } | null;
  onSetError: (message: string | null) => void;
  onSelectSettingsScope: (id: string) => void;
  onOpenStageCreateDialog: () => void;
  onRefreshStages: () => void;
  onUpdateAppKey: (event: React.FormEvent<HTMLFormElement>) => void | Promise<void>;
  onEnsureDeployKey: () => void | Promise<void>;
  onUpdateSelfApp: (next: boolean) => void | Promise<void>;
  onUpdateTemplateApp: (next: boolean) => void | Promise<void>;
  onUpdateInfrastructureHelper: (next: boolean) => void | Promise<void>;
  onApproveInfrastructureCommandCatalog: () => void | Promise<void>;
  onDenyInfrastructureCommandCatalog: () => void | Promise<void>;
  onLoadGitlabCiPreview: () => void | Promise<void>;
  onDeleteStage: (stageId: string, stageName: string) => void | Promise<void>;
  onEnsureStageDomain: (stage: AppStage) => void | Promise<void>;
  onUpdateStageSettings: (
    stage: AppStage,
    next: {
      production_server_id: string | null;
      conversation_server_id: string | null;
      dns_record_name: string | null;
      gitlab_environment_name: string | null;
      app_dns_record_name?: string | undefined;
    }
  ) => void | Promise<void>;
  onUpdateAppTargets: (next: {
    production_server_id: string | null;
    conversation_server_id: string | null;
    conversation_executor_policy: 'auto' | 'system';
  }) => void | Promise<void>;
  onUseWorkerTagForConversations: (tag: string) => void | Promise<void>;
  onRotateSecret: (key: string) => void | Promise<void>;
  onDeleteStageSecret: (secretId: string) => void | Promise<void>;
  onUpdateStageSecret: (secretId: string) => void | Promise<void>;
  onCreateStageSecret: () => void | Promise<void>;
  onMoveStageSecret: (secretId: string, targetStageId: string | null) => void | Promise<void>;
  onSetEditingSecretId: (secretId: string | null) => void;
  onSetEditSecretValue: (value: string) => void;
  onSetEditSecretSourceType: (value: 'manual' | 'external') => void;
  onSetEditExternalAppId: (value: string) => void;
  onSetEditExternalStageMode: (value: 'fixed' | 'production' | 'same_stage') => void;
  onSetEditExternalStageId: (value: string) => void;
  onSetEditExternalSecretKey: (value: string) => void;
  onSetNewSecretKey: (value: string) => void;
  onSetNewSecretType: (value: 'env' | 'file') => void;
  onSetNewSecretDescription: (value: string) => void;
  onSetNewSecretValue: (value: string) => void;
  onSetNewSecretSourceType: (value: 'manual' | 'external') => void;
  onSetNewExternalAppId: (value: string) => void;
  onSetNewExternalStageMode: (value: 'fixed' | 'production' | 'same_stage') => void;
  onSetNewExternalStageId: (value: string) => void;
  onSetNewExternalSecretKey: (value: string) => void;
  onLoadUnifiedPreview: () => void | Promise<void>;
  onSetAutofixEnabled: (value: boolean) => void;
  onSetAutofixFeatureBranches: (value: boolean) => void;
  onSetAutofixPrompt: (value: string) => void;
  onSaveAutofix: () => void | Promise<void>;
  onResetAutofixTemplate: () => void;
  onSetAppAutopilotMode: (value: import('../types').AutopilotMode) => void;
  onSetAppEnablePushReminder: (value: boolean) => void;
  onSetAppTemplateBuildFailed: (value: string) => void;
  onSetAppTemplateMergeConflict: (value: string) => void;
  onSetAppTemplateReadySemi: (value: string) => void;
  onSetAppTemplateReadyFull: (value: string) => void;
  onSaveAppAutopilot: () => void | Promise<void>;
  onUpdatePipelineWaitAllowed: (value: boolean) => void | Promise<void>;
  onSetAutoMrFeedback: (value: boolean) => void;
  onSaveMrFeedback: () => void | Promise<void>;
  onAddGitlabHook: () => void | Promise<void>;
  onRemoveGitlabHook: () => void | Promise<void>;
  onSetSelectedContainer: (value: string) => void;
  onRestartContainers: (container?: string) => void | Promise<void>;
  onSetBackgroundJobResult: (
    value: { message: string; job?: import('./BackgroundJobButton').JobState } | null
  ) => void;
  onDismissStageError: () => void;
  onDismissTargetError: () => void;
  // Identity services
  identityServices: IdentityServiceSummary[];
  identityServicesBusy: boolean;
  identityResult: string | null;
  ymlIdConfigured: boolean;
  identityHealth: IdentityHealthResponse | null;
  identityHealthBusy: boolean;
  onReconcileIdentity: (serviceName: string) => void | Promise<void>;
  onDeprovisionIdentity: (serviceName: string) => void | Promise<void>;
  onManualIdentitySecrets: (serviceName: string, clientId: string, clientSecret: string) => void | Promise<void>;
  onCheckIdentityHealth: () => void | Promise<void>;
}) {
  const settingsStageDns = splitDnsRecordName(settingsStageSnapshot.dnsRecord, dnsDomains);
  const settingsStageDnsDomain =
    settingsStageDns.domain || (settingsStageDns.subdomain ? '' : dnsDefault ?? '');
  const stageDomainOptions = dnsDomains.map((domain) => (
    <option key={domain} value={domain}>
      {domain}
    </option>
  ));
  const workerTags = workerTagOptions(workerNodes);
  const [sourceStagesByApp, setSourceStagesByApp] = useState<Record<string, AppStage[]>>({});
  const [sourceSecretsByLookup, setSourceSecretsByLookup] = useState<Record<string, AppStageSecret[]>>({});
  const stageSecretMoveTargets = useMemo(
    () =>
      stages
        .slice()
        .sort((a, b) => {
          if (a.is_default !== b.is_default) {
            return a.is_default ? -1 : 1;
          }
          return a.name.localeCompare(b.name);
        }),
    [stages]
  );
  const [moveTargetStageId, setMoveTargetStageId] = useState('');
  const defaultMoveTargetStageId = stageSecretMoveTargets[0]?.id ?? '';
  const effectiveMoveTargetStageId = stageSecretMoveTargets.some(
    (stage) => stage.id === moveTargetStageId
  )
    ? moveTargetStageId
    : defaultMoveTargetStageId;

  const availableExternalApps = useMemo(
    () => availableApps.filter((candidate) => candidate.id !== appId),
    [availableApps, appId]
  );

  const ensureSourceStages = useCallback(async (sourceAppId: string) => {
    if (!sourceAppId || sourceStagesByApp[sourceAppId]) {
      return;
    }
    const data = await apiClient.get<{ stages: AppStage[] }>(`/api/apps/${sourceAppId}/stages`);
    setSourceStagesByApp((prev) => ({ ...prev, [sourceAppId]: data.stages ?? [] }));
  }, [apiClient, sourceStagesByApp]);

  const ensureSourceSecrets = useCallback(async (sourceAppId: string, stageId: string | null) => {
    if (!sourceAppId) {
      return;
    }
    const lookupKey = `${sourceAppId}:${stageId ?? 'general'}`;
    if (sourceSecretsByLookup[lookupKey]) {
      return;
    }
    const params = stageId ? `?stage_id=${stageId}` : '';
    const data = await apiClient.get<AppStageSecretsResponse>(`/api/apps/${sourceAppId}/secrets${params}`);
    setSourceSecretsByLookup((prev) => ({ ...prev, [lookupKey]: data.secrets ?? [] }));
  }, [apiClient, sourceSecretsByLookup]);

  const resolvedExternalLookupStage = useCallback((
    sourceAppId: string,
    stageMode: 'fixed' | 'production' | 'same_stage',
    fixedStageId: string
  ): string | null => {
    const sourceStages = sourceStagesByApp[sourceAppId] ?? [];
    if (stageMode === 'fixed') {
      return fixedStageId || null;
    }
    if (stageMode === 'production') {
      return sourceStages.find((stage) => stage.is_default)?.id ?? null;
    }
    return sourceStages.find((stage) => stage.name === settingsStageSnapshot.name)?.id ?? null;
  }, [settingsStageSnapshot.name, sourceStagesByApp]);

  useEffect(() => {
    const ids = [newExternalAppId, editExternalAppId].filter(Boolean);
    ids.forEach((sourceAppId) => {
      void ensureSourceStages(sourceAppId);
    });
  }, [newExternalAppId, editExternalAppId, ensureSourceStages]);

  useEffect(() => {
    const lookups = [
      {
        appId: newExternalAppId,
        stageId: resolvedExternalLookupStage(newExternalAppId, newExternalStageMode, newExternalStageId)
      },
      {
        appId: editExternalAppId,
        stageId: resolvedExternalLookupStage(editExternalAppId, editExternalStageMode, editExternalStageId)
      }
    ];
    lookups.forEach((lookup) => {
      if (lookup.appId) {
        void ensureSourceSecrets(lookup.appId, lookup.stageId);
      }
    });
  }, [
    newExternalAppId,
    newExternalStageMode,
    newExternalStageId,
    editExternalAppId,
    editExternalStageMode,
    editExternalStageId,
    sourceStagesByApp,
    ensureSourceSecrets,
    resolvedExternalLookupStage,
  ]);

  function renderExternalSecretFields(kind: 'new' | 'edit', secretType: 'env' | 'file') {
    const sourceAppId = kind === 'new' ? newExternalAppId : editExternalAppId;
    const stageMode = kind === 'new' ? newExternalStageMode : editExternalStageMode;
    const fixedStageId = kind === 'new' ? newExternalStageId : editExternalStageId;
    const sourceKey = kind === 'new' ? newExternalSecretKey : editExternalSecretKey;
    const setSourceAppId = kind === 'new' ? onSetNewExternalAppId : onSetEditExternalAppId;
    const setStageMode = kind === 'new' ? onSetNewExternalStageMode : onSetEditExternalStageMode;
    const setFixedStageId = kind === 'new' ? onSetNewExternalStageId : onSetEditExternalStageId;
    const setSourceKey = kind === 'new' ? onSetNewExternalSecretKey : onSetEditExternalSecretKey;
    const sourceStages = sourceStagesByApp[sourceAppId] ?? [];
    const lookupStageId = resolvedExternalLookupStage(sourceAppId, stageMode, fixedStageId);
    const secretOptions = sourceSecretsByLookup[`${sourceAppId}:${lookupStageId ?? 'general'}`] ?? [];
    const filteredSecretOptions = secretOptions.filter((secret) => secret.secret_type === secretType);
    const listId = `${kind}-external-secret-options`;

    return (
      <div className="stack">
        <label className="row">
          <span style={{ minWidth: '120px' }}>Source app:</span>
          <select value={sourceAppId} onChange={(event) => setSourceAppId(event.target.value)}>
            <option value="">Select source app</option>
            {availableExternalApps.map((sourceApp) => (
              <option key={sourceApp.id} value={sourceApp.id}>
                {sourceApp.name}
              </option>
            ))}
          </select>
        </label>
        <label className="row">
          <span style={{ minWidth: '120px' }}>Source stage:</span>
          <select
            value={stageMode}
            onChange={(event) => setStageMode(event.target.value as 'fixed' | 'production' | 'same_stage')}
          >
            <option value="fixed">Fixed stage/general</option>
            <option value="production">Production stage</option>
            <option value="same_stage">Same stage name</option>
          </select>
        </label>
        {stageMode === 'fixed' && (
          <label className="row">
            <span style={{ minWidth: '120px' }}>Fixed stage:</span>
            <select value={fixedStageId} onChange={(event) => setFixedStageId(event.target.value)}>
              <option value="">General</option>
              {sourceStages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="row">
          <span style={{ minWidth: '120px' }}>Source key:</span>
          <input
            list={listId}
            value={sourceKey}
            onChange={(event) => setSourceKey(event.target.value)}
            placeholder="Source secret key"
          />
          <datalist id={listId}>
            {filteredSecretOptions.map((secret) => (
              <option key={secret.id} value={secret.key} />
            ))}
          </datalist>
        </label>
        <p className="hint">
          External secrets are resolved only during preview/apply. The value is not copied into this
          app.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="card full">
        <div className="card-header">
          <h3>Settings Scope</h3>
          <div className="row">
            <button onClick={() => void onRefreshStages()} disabled={stageBusy}>
              Refresh
            </button>
          </div>
        </div>
        <StageTabs
          tabs={settingsScopeTabs}
          selectedId={selectedSettingsStageId}
          onSelect={(id) => {
            if (id === '__add_stage__') {
              onOpenStageCreateDialog();
              return;
            }
            onSelectSettingsScope(id);
          }}
        />
        <p className="status">
          General settings apply to all stages. Stage tabs (production and additional stages) hold
          stage-specific deployment settings and overrides.
        </p>
      </div>

      {settingsScopeIsGeneral && (
        <div className="card full">
          <div className="card-header">
            <h3>General App Settings</h3>
          </div>
          <form onSubmit={onUpdateAppKey} className="stack">
            <div className="row wrap">
              <input
                name="gitlab_url"
                type="text"
                inputMode="url"
                placeholder="GitLab URL or SSH remote"
                defaultValue={app.gitlab_url ?? ''}
                style={{ minWidth: '280px' }}
              />
              <input
                name="slug"
                placeholder="App slug"
                defaultValue={app.slug}
                style={{ minWidth: '220px' }}
              />
              <input
                name="gitlab_ref"
                placeholder="GitLab ref (blank = global default)"
                defaultValue={app.gitlab_ref ?? ''}
                style={{ minWidth: '220px' }}
              />
              <button type="submit">Update</button>
            </div>
            <div className="hint">
              Admin fields for GitLab defaults. GitLab URL accepts HTTPS URLs and SSH remotes
              such as git@gitlab.com:group/repo.git. Leave ref empty to use the global default ref.
              Slug is edited here. DNS values are edited in stage settings.
            </div>
          </form>

          <form
            key={`${app.production_server_id ?? ''}:${app.conversation_server_id ?? ''}:${app.conversation_executor_policy ?? 'auto'}`}
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              void onUpdateAppTargets({
                production_server_id: String(form.get('production_server_id') ?? '') || null,
                conversation_server_id: String(form.get('conversation_server_id') ?? '') || null,
                conversation_executor_policy:
                  String(form.get('conversation_executor_policy') ?? 'auto') === 'system' ? 'system' : 'auto'
              });
            }}
          >
            <h3>Workspace Targets</h3>
            <div className="row wrap">
              <label>
                Production server
                <select name="production_server_id" defaultValue={app.production_server_id ?? ''}>
                  <option value="">No app default</option>
                  {servers.map((server) => (
                    <option key={server.id} value={server.id}>
                      {serverOptionLabel(server)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Conversation server
                <select name="conversation_server_id" defaultValue={app.conversation_server_id ?? ''}>
                  <option value="">No app default</option>
                  {servers.map((server) => (
                    <option key={server.id} value={server.id}>
                      {serverOptionLabel(server)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Executor fallback
                <select
                  name="conversation_executor_policy"
                  defaultValue={app.conversation_executor_policy ?? 'auto'}
                >
                  <option value="auto">Own executor, then system default</option>
                  <option value="system">System default</option>
                </select>
              </label>
              <button type="submit" disabled={targetBusy}>
                {targetBusy ? 'Saving...' : 'Save targets'}
              </button>
            </div>
            {workerTags.length > 0 && (
              <div className="row wrap">
                {workerTags.map((option) => (
                  <button
                    key={option.tag}
                    type="button"
                    className="ghost compact"
                    disabled={targetBusy}
                    onClick={() => void onUseWorkerTagForConversations(option.tag)}
                  >
                    Use executor {option.label}
                  </button>
                ))}
              </div>
            )}
            <div className="status">
              <div className="row">
                <span>Production server</span>
                <span>{detail.production_server?.name ?? '—'}</span>
              </div>
              <div className="row">
                <span>Conversation server</span>
                <span>{detail.conversation_server?.name ?? '—'}</span>
              </div>
              <div className="row">
                <span>Executor fallback</span>
                <span>{app.conversation_executor_policy === 'system' ? 'System default' : 'Own executor, then system default'}</span>
              </div>
            </div>
            {targetError && <ErrorBanner message={targetError} onDismiss={onDismissTargetError} />}
            {targetResult && <p className="status">{targetResult}</p>}
          </form>

          <div className="status">
            <strong>Admin details</strong>
            <div className="row">
              <span>Slug</span>
              <span className="mono">{app.slug}</span>
            </div>
            <div className="row">
              <span>Repo path</span>
              <span className="mono">{app.repo_path ?? '—'}</span>
            </div>
            <div className="row">
              <span>Agent manifest</span>
              <span className="text-muted">
                Add <code style={{ fontSize: '0.9em' }}>deploy/agent.yaml</code> in repo to
                customize
              </span>
            </div>
            <div className="row">
              <span>Containers</span>
              <span className="mono">{app.container_names.join(', ') || '—'}</span>
            </div>
            <div className="row">
              <span>Workspace TTL</span>
              <span>{detail.workspace_ttl_hours ? `${detail.workspace_ttl_hours}h` : '—'}</span>
            </div>
            <div className="row">
              <span>Workspace root</span>
              <span className="mono">{detail.workspace_root ?? '—'}</span>
            </div>
            <div className="row">
              <span>GitLab deploy key</span>
              <span>{deployKeyStatus ? deployKeyStatus.status.replace(/_/g, ' ') : '—'}</span>
            </div>
            {deployKeyStatus?.message && <div className="error-inline">{deployKeyStatus.message}</div>}
            {deployKeyStatus && deployKeyStatus.status !== 'ok' && deployKeyStatus.can_fix && (
              <button
                type="button"
                className="ghost"
                onClick={() => void onEnsureDeployKey()}
                disabled={deployKeyBusy}
              >
                {deployKeyBusy ? 'Adding key...' : 'Add deploy key'}
              </button>
            )}
            <span className="hint">
              Internal metadata used for workspace mounts and runtime automation.
            </span>
          </div>

          <div className="stack">
            <label className="row">
              <input
                type="checkbox"
                checked={app.is_self ?? false}
                onChange={(e) => void onUpdateSelfApp(e.target.checked)}
                disabled={selfBusy}
              />
              <span>Mark as Mission Control (self)</span>
              <span className="hint">Used for internal log context</span>
            </label>
            {selfResult && <p className="status">{selfResult}</p>}
            <label className="row">
              <input
                type="checkbox"
                checked={app.is_template ?? false}
                onChange={(e) => void onUpdateTemplateApp(e.target.checked)}
                disabled={templateBusy}
              />
              <span>Expose as template</span>
              <span className="hint">Templates appear in the new app picker</span>
            </label>
            {templateResult && <p className="status">{templateResult}</p>}
            <label className="row">
              <input
                type="checkbox"
                checked={app.infrastructure_helper ?? false}
                onChange={(e) => void onUpdateInfrastructureHelper(e.target.checked)}
                disabled={infraHelperBusy}
              />
              <span>Infrastructure helper repo</span>
              <span className="hint">Allows infra-targeting workflows for trusted admin use</span>
            </label>
            {detail.infrastructure_command_catalog && (
              <div className="status">
                <div>{detail.infrastructure_command_catalog.message}</div>
                <div className="row">
                  <span>Approved</span>
                  <span className="mono">
                    {detail.infrastructure_command_catalog.approved_git_ref?.slice(0, 12) ?? '—'}
                  </span>
                </div>
                <div className="row">
                  <span>Current</span>
                  <span className="mono">
                    {detail.infrastructure_command_catalog.current_git_ref?.slice(0, 12) ?? '—'}
                  </span>
                </div>
                {detail.infrastructure_command_catalog.can_approve &&
                  ['approval_required', 'update_available', 'denied'].includes(
                    detail.infrastructure_command_catalog.status
                  ) && (
                    <div className="row">
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => void onApproveInfrastructureCommandCatalog()}
                        disabled={infraCatalogBusy}
                      >
                        {infraCatalogBusy ? 'Working...' : 'Approve current command catalog'}
                      </button>
                      {detail.infrastructure_command_catalog.status !== 'denied' && (
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => void onDenyInfrastructureCommandCatalog()}
                          disabled={infraCatalogBusy}
                        >
                          Dismiss current update
                        </button>
                      )}
                    </div>
                )}
              </div>
            )}
            {infraHelperResult && <p className="status">{infraHelperResult}</p>}
            <label className="row">
              <input
                type="checkbox"
                checked={pipelineWaitAllowed}
                onChange={(e) => void onUpdatePipelineWaitAllowed(e.target.checked)}
                disabled={pipelineWaitBusy}
              />
              <span>Allow agents to wait for pipelines</span>
              <span className="hint">Changes stop behavior to ask the agent to stop waiting first</span>
            </label>
            {pipelineWaitResult && <p className="status">{pipelineWaitResult}</p>}
          </div>

          <div className="stack">
            <p className="status">
              Set a GitLab environment on each intended deployment stage, then preview the generated .gitlab-ci.yml.
            </p>
            <div className="row">
              <button
                onClick={() => void onLoadGitlabCiPreview()}
                disabled={
                  gitlabCiBusy ||
                  !stages.some(
                    (stage) =>
                      stage.gitlab_environment_name !== null &&
                      stage.gitlab_environment_name !== ''
                  )
                }
              >
                {gitlabCiBusy ? 'Loading...' : 'Preview GitLab CI config'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!settingsScopeIsGeneral && (
        <div className="card">
          <div className="card-header">
            <h3>Stage Configuration</h3>
          </div>
          <div className="stack">
            <p className="status">
              Each stage has its own containers, domain, and deployment artifacts. Production is
              the default stage.
            </p>
            <div className="row">
              <span>
                <strong>{settingsStageSnapshot.name}</strong>
                {settingsStageSnapshot.isDefault && <span className="badge">Default</span>}
              </span>
              {!settingsStageSnapshot.isDefault && settingsStageSnapshot.stage && (() => {
                const stage = settingsStageSnapshot.stage;
                return (
                  <button
                    type="button"
                    className="ghost compact"
                    onClick={() => void onDeleteStage(stage.id, stage.name)}
                    disabled={stageBusy}
                  >
                    Delete
                  </button>
                );
              })()}
            </div>
            <div className="status">
              <div className="row">
                <span>Slug suffix:</span>
                <span className="mono">{settingsStageSnapshot.slugSuffix || '—'}</span>
              </div>
              <div className="row">
                <span>GitLab environment:</span>
                <span className="mono">
                  {settingsStageSnapshot.gitlabEnvironmentName ?? 'Not enabled for generated CI'}
                </span>
              </div>
              <div className="row">
                <span>DNS:</span>
                <span className="mono">{settingsStageSnapshot.dnsRecord ?? '—'}</span>
                {settingsStageSnapshot.stage && settingsStageSnapshot.dnsRecord && (
                  <button
                    type="button"
                    className="ghost compact"
                    onClick={() => void onEnsureStageDomain(settingsStageSnapshot.stage!)}
                    disabled={stageBusy}
                    title="Repair this stage's configured DNS only; does not deploy or change login settings"
                  >
                    {stageBusy ? 'Repairing...' : 'Repair domain DNS'}
                  </button>
                )}
              </div>
              <div className="row">
                <span>Containers:</span>
                <span className="mono">{settingsStageSnapshot.containers.join(', ') || '—'}</span>
              </div>
            </div>
            {settingsStageSnapshot.stage && (
              <form
                key={settingsStageSnapshot.stage.id}
                className="stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  const stageDnsDomain = String(form.get('stage_dns_domain') ?? '').trim();
                  const stageDnsSubdomain = String(form.get('stage_dns_subdomain') ?? '').trim();
                  const stageDnsUseApex = form.get('stage_dns_use_apex') === 'on';
                  const stageDnsRecordOverride =
                    String(form.get('stage_dns_record_name') ?? '').trim();
                  const stageDnsRecordName =
                    stageDnsDomain && stageDnsUseApex
                      ? stageDnsDomain
                      : stageDnsDomain && stageDnsSubdomain
                      ? `${stageDnsSubdomain}.${stageDnsDomain}`
                      : stageDnsSubdomain.includes('.')
                        ? stageDnsSubdomain
                        : stageDnsRecordOverride || null;
                  const appDnsChanged =
                    settingsStageSnapshot.isDefault &&
                    stageDnsRecordName &&
                    stageDnsRecordName !== (app.dns_record_name ?? null)
                      ? stageDnsRecordName
                      : undefined;
                  void onUpdateStageSettings(settingsStageSnapshot.stage!, {
                    production_server_id:
                      String(form.get('stage_production_server_id') ?? '') || null,
                    conversation_server_id:
                      String(form.get('stage_conversation_server_id') ?? '') || null,
                    dns_record_name: stageDnsRecordName,
                    gitlab_environment_name:
                      String(form.get('stage_gitlab_environment_name') ?? '').trim() || null,
                    app_dns_record_name: appDnsChanged
                  });
                }}
              >
                <div className="row wrap">
                  <select
                    name="stage_production_server_id"
                    defaultValue={settingsStageSnapshot.stage.production_server_id ?? ''}
                  >
                    <option value="">Inherit production server</option>
                    {servers.map((server) => (
                      <option key={server.id} value={server.id}>
                        {serverOptionLabel(server)}
                      </option>
                    ))}
                  </select>
                  <select
                    name="stage_conversation_server_id"
                    defaultValue={settingsStageSnapshot.stage.conversation_server_id ?? ''}
                  >
                    <option value="">Inherit conversation server</option>
                    {servers.map((server) => (
                      <option key={server.id} value={server.id}>
                        {serverOptionLabel(server)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="row wrap">
                  <select
                    name="stage_dns_domain"
                    defaultValue={settingsStageDnsDomain}
                    disabled={dnsDomains.length === 0}
                  >
                    <option value="">
                      {dnsDomains.length === 0 ? 'No DNS domains available' : 'DNS domain'}
                    </option>
                    {stageDomainOptions}
                  </select>
                  <input
                    name="stage_dns_subdomain"
                    placeholder={
                      settingsStageSnapshot.isDefault
                        ? 'Subdomain (production)'
                        : `Subdomain (${settingsStageSnapshot.name})`
                    }
                    defaultValue={settingsStageDns.subdomain}
                  />
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      name="stage_dns_use_apex"
                      defaultChecked={Boolean(settingsStageDns.domain && !settingsStageDns.subdomain)}
                    />
                    <span>Use apex</span>
                  </label>
                  <input
                    name="stage_dns_record_name"
                    placeholder="DNS record override (optional)"
                    defaultValue={settingsStageSnapshot.stage.dns_record_name ?? ''}
                  />
                  <input
                    name="stage_gitlab_environment_name"
                    placeholder="GitLab environment (enables CI deploy)"
                    defaultValue={settingsStageSnapshot.stage.gitlab_environment_name ?? ''}
                  />
                  <button type="submit" disabled={stageBusy}>
                    {stageBusy ? 'Saving...' : 'Save stage settings'}
                  </button>
                </div>
              </form>
            )}
            {additionalStages.length === 0 && (
              <p className="status">No additional stages configured.</p>
            )}
            {stageError && <ErrorBanner message={stageError} onDismiss={onDismissStageError} />}
            {stageResult && <p className="status">{stageResult}</p>}
          </div>
        </div>
      )}

      {settingsScopeIsGeneral && (
        <div className="card">
          <div className="card-header">
            <h3>Ops secrets (from manifest)</h3>
          </div>
          <div className="stack">
            {!opsOverview || !opsOverview.present ? (
              <p className="status">
                {opsOverview?.missing_reason || 'No deploy/ops.yaml found for this app.'}
              </p>
            ) : (
              <>
                {opsResult && <p className="status">{opsResult}</p>}
                {opsOverview.secrets.length === 0 ? (
                  <p className="status">No secrets configured in ops.yaml.</p>
                ) : (
                  <ul className="list">
                    {opsOverview.secrets.map((secret) => (
                      <li key={secret.key}>
                        <div className="row">
                          <span className="mono">{secret.key}</span>
                          <button
                            type="button"
                            className="ghost compact"
                            onClick={() => void onRotateSecret(secret.key)}
                            disabled={opsBusyKey === secret.key}
                          >
                            {opsBusyKey === secret.key ? 'Rotating...' : 'Rotate'}
                          </button>
                        </div>
                        <div className="status">
                          {secret.apply_script
                            ? `Script: ${secret.apply_script}`
                            : 'No apply script'}
                        </div>
                        {secret.generate?.length && (
                          <div className="status">
                            Generate: {secret.generate.kind ?? 'random'} ({secret.generate.length})
                          </div>
                        )}
                        {secret.restart_services && secret.restart_services.length > 0 && (
                          <div className="status">
                            Restart: {secret.restart_services.join(', ')}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {!settingsScopeIsGeneral && (
        <IdentityServicesCard
          identityServices={identityServices}
          identityServicesBusy={identityServicesBusy}
          identityResult={identityResult}
          ymlIdConfigured={ymlIdConfigured}
          identityHealth={identityHealth}
          identityHealthBusy={identityHealthBusy}
          onReconcileIdentity={onReconcileIdentity}
          onDeprovisionIdentity={onDeprovisionIdentity}
          onManualIdentitySecrets={onManualIdentitySecrets}
          onCheckIdentityHealth={onCheckIdentityHealth}
        />
      )}

      <div className="card">
        <div className="card-header">
          <h3>Stage Secrets</h3>
          <span className="hint">Scope: {settingsSecretScopeLabel}</span>
        </div>
        <div className="stack">
          <p className="status">
            Manage secrets from <span className="mono">deploy/ops.yaml</span>. Secret values are
            encrypted. Stage overrides are optional; general values are inherited by all stages.
          </p>
          {settingsSecretScope.scope === 'stage' && (
            <p className="status">
              Overrides apply only to {settingsStageSnapshot.name}. Missing keys fall back to
              general.
            </p>
          )}
          {!settingsScopeIsGeneral && settingsStageSnapshot.isDefault && (
            <p className="status">
              Production currently inherits general secrets. Edit in the General tab to change
              production values.
            </p>
          )}
          {settingsScopeIsGeneral && stageSecretMoveTargets.length > 0 && (
            <label className="row">
              <span>Move target:</span>
              <select
                value={effectiveMoveTargetStageId}
                onChange={(event) => setMoveTargetStageId(event.target.value)}
              >
                {stageSecretMoveTargets.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {stageSecretsResult && <p className="status">{stageSecretsResult}</p>}

          {stageSecretsBusy && !stageSecrets.length ? (
            <p className="status">Loading...</p>
          ) : stageSecrets.length > 0 ? (
            <ul className="list">
              {stageSecrets.map((secret) => {
                const manifestSecretSpec = manifestSecrets.find((spec) => spec.key === secret.key);
                const displayFilePath = manifestSecretSpec?.file_path;
                const displayFileMode = manifestSecretSpec?.file_mode ?? '0600';
                const displayFileOwner = manifestSecretSpec?.file_owner;
                return (
                  <li key={secret.id}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <div className="stack">
                        <div className="row">
                          <span className="mono">{secret.key}</span>
                          <span className="hint">{secret.secret_type}</span>
                          {secret.has_value && <span className="hint">(has value)</span>}
                          {secret.source_type === 'external' && <span className="hint">(external)</span>}
                        </div>
                        {secret.external_source && (
                          <div className="status">
                            Source:{' '}
                            <span className="mono">
                              {secret.external_source.app_name ?? secret.external_source.app_id} /{' '}
                              {secret.external_source.stage_mode === 'same_stage'
                                ? 'same stage'
                                : secret.external_source.stage_name ??
                                  (secret.external_source.stage_mode === 'production'
                                    ? 'production'
                                    : 'general')}{' '}
                              / {secret.external_source.secret_key}
                            </span>
                          </div>
                        )}
                        {secret.secret_type === 'file' && displayFilePath && (
                          <div className="status">
                            Path: {displayFilePath} ({displayFileMode})
                            {displayFileOwner ? ` owner ${displayFileOwner}` : ''}
                          </div>
                        )}
                        {secret.description && <div className="status">{secret.description}</div>}
                      </div>
                      <div className="row">
                        {settingsScopeIsGeneral ? (
                          <button
                            type="button"
                            className="ghost compact"
                            onClick={() =>
                              void onMoveStageSecret(secret.id, effectiveMoveTargetStageId)
                            }
                            disabled={stageSecretsBusy || !effectiveMoveTargetStageId}
                          >
                            Move to Stage
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="ghost compact"
                            onClick={() => void onMoveStageSecret(secret.id, null)}
                            disabled={stageSecretsBusy}
                          >
                            Move to General
                          </button>
                        )}
                        <button
                          type="button"
                          className="ghost compact"
                          onClick={() => {
                            onSetEditingSecretId(secret.id);
                            onSetEditSecretValue('');
                            onSetEditSecretSourceType(secret.source_type ?? 'manual');
                            onSetEditExternalAppId(secret.external_source?.app_id ?? '');
                            onSetEditExternalStageMode(secret.external_source?.stage_mode ?? 'fixed');
                            onSetEditExternalStageId(secret.external_source?.stage_id ?? '');
                            onSetEditExternalSecretKey(secret.external_source?.secret_key ?? '');
                          }}
                          disabled={stageSecretsBusy}
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="ghost compact"
                          onClick={() => void onDeleteStageSecret(secret.id)}
                          disabled={stageSecretsBusy}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {editingSecretId === secret.id && (
                      <div className="stack" style={{ marginTop: '0.5rem' }}>
                        <label className="row">
                          <span style={{ minWidth: '120px' }}>Value source:</span>
                          <select
                            value={editSecretSourceType}
                            onChange={(event) =>
                              onSetEditSecretSourceType(event.target.value as 'manual' | 'external')
                            }
                          >
                            <option value="manual">Manual encrypted value</option>
                            <option value="external">External secret</option>
                          </select>
                        </label>
                        {editSecretSourceType === 'external' ? (
                          renderExternalSecretFields('edit', secret.secret_type)
                        ) : (
                          <label className="stack">
                            <span>New value:</span>
                            {secret.secret_type === 'file' ? (
                              <textarea
                                rows={3}
                                value={editSecretValue}
                                onChange={(e) => onSetEditSecretValue(e.target.value)}
                                placeholder="Enter new secret file content..."
                              />
                            ) : (
                              <input
                                type="password"
                                autoComplete="new-password"
                                value={editSecretValue}
                                onChange={(e) => onSetEditSecretValue(e.target.value)}
                                placeholder="Leave blank to keep current value..."
                              />
                            )}
                          </label>
                        )}
                        <div className="row">
                          <button
                            type="button"
                            onClick={() => void onUpdateStageSecret(secret.id)}
                            disabled={
                              stageSecretsBusy ||
                              (editSecretSourceType === 'manual' &&
                                secret.source_type === 'external' &&
                                !editSecretValue.trim()) ||
                              (editSecretSourceType === 'external' &&
                                (!editExternalAppId || !editExternalSecretKey.trim()))
                            }
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => {
                              onSetEditingSecretId(null);
                              onSetEditSecretValue('');
                              onSetEditSecretSourceType('manual');
                              onSetEditExternalAppId('');
                              onSetEditExternalStageMode('fixed');
                              onSetEditExternalStageId('');
                              onSetEditExternalSecretKey('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="status">No secrets stored for this scope.</p>
          )}

          <details>
            <summary>Add new secret</summary>
            <div className="stack" style={{ marginTop: '0.5rem' }}>
              <label className="row">
                <span style={{ minWidth: '100px' }}>Key:</span>
                <select
                  value={effectiveNewSecretKey}
                  onChange={(e) => {
                    const key = e.target.value;
                    onSetNewSecretKey(key);
                    const spec = manifestSecrets.find((secret) => secret.key === key);
                    if (!spec) {
                      return;
                    }
                    const specType = (spec.secret_type ?? 'env') as 'env' | 'file';
                    onSetNewSecretType(specType);
                    if (spec.description) {
                      onSetNewSecretDescription(spec.description);
                    }
                  }}
                  disabled={!availableManifestSecrets.length}
                >
                  {!availableManifestSecrets.length && (
                    <option value="">All manifest secrets already configured in this scope</option>
                  )}
                  {availableManifestSecrets.map((secret) => (
                    <option key={secret.key} value={secret.key}>
                      {secret.key}
                    </option>
                  ))}
                </select>
              </label>
              <label className="row">
                <span style={{ minWidth: '100px' }}>Type:</span>
                <span className="mono">{selectedNewSecretType}</span>
              </label>
              <label className="row">
                <span style={{ minWidth: '100px' }}>Value source:</span>
                <select
                  value={newSecretSourceType}
                  onChange={(event) =>
                    onSetNewSecretSourceType(event.target.value as 'manual' | 'external')
                  }
                >
                  <option value="manual">Manual encrypted value</option>
                  <option value="external">External secret</option>
                </select>
              </label>
              {selectedNewSecretType === 'file' && (
                <>
                  <div className="status">
                    File path:{' '}
                    <span className="mono">
                      {selectedManifestFilePath || 'Not set in deploy/ops.yaml'}
                    </span>
                  </div>
                  <p className="hint" style={{ marginTop: '-0.5rem' }}>
                    File path is defined in <span className="mono">deploy/ops.yaml</span>.
                  </p>
                  <div className="status">
                    Mode: <span className="mono">{selectedNewSecretSpec?.file_mode ?? '0600'}</span>
                    {selectedNewSecretSpec?.file_owner ? (
                      <>
                        {' '}
                        | Owner: <span className="mono">{selectedNewSecretSpec.file_owner}</span>
                      </>
                    ) : null}
                  </div>
                  <label className="row">
                    <span style={{ minWidth: '100px' }}>Upload file:</span>
                    <input
                      type="file"
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        if (!file) {
                          return;
                        }
                        const content = await file.text();
                        onSetNewSecretValue(content);
                        if (!newSecretDescription.trim()) {
                          onSetNewSecretDescription(`Imported from ${file.name}`);
                        }
                      }}
                    />
                  </label>
                </>
              )}
              {newSecretSourceType === 'external' ? (
                renderExternalSecretFields('new', selectedNewSecretType)
              ) : (
                <label className="stack">
                  <span>Value:</span>
                  {selectedNewSecretType === 'file' ? (
                    <textarea
                      rows={3}
                      value={newSecretValue}
                      onChange={(e) => onSetNewSecretValue(e.target.value)}
                      placeholder="Enter secret file content..."
                    />
                  ) : (
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={newSecretValue}
                      onChange={(e) => onSetNewSecretValue(e.target.value)}
                      placeholder="Enter secret value..."
                    />
                  )}
                </label>
              )}
              <label className="row">
                <span style={{ minWidth: '100px' }}>Description:</span>
                <input
                  type="text"
                  value={newSecretDescription}
                  onChange={(e) => onSetNewSecretDescription(e.target.value)}
                  placeholder="Optional description"
                />
              </label>
              <button
                type="button"
                onClick={() => void onCreateStageSecret()}
                disabled={
                  stageSecretsBusy ||
                  !effectiveNewSecretKey.trim() ||
                  (newSecretSourceType === 'manual' &&
                    !newSecretValue.trim() &&
                    selectedNewSecretSpec?.required !== false) ||
                  (newSecretSourceType === 'external' &&
                    (!newExternalAppId || !newExternalSecretKey.trim()))
                }
              >
                {stageSecretsBusy ? 'Saving...' : 'Add Secret'}
              </button>
            </div>
          </details>
        </div>
      </div>

      {!settingsScopeIsGeneral && (
        <div className="card">
          <div className="card-header">
            <h3>Unified Deploy Preview</h3>
            <span className="hint">For stage: {settingsStageSnapshot.name}</span>
          </div>
          <div className="stack">
            <p className="status">
              Preview and apply all deployment files (scripts, configs, secrets) in one action.
            </p>
            <div className="row">
              <button onClick={() => void onLoadUnifiedPreview()} disabled={unifiedPreviewBusy}>
                {unifiedPreviewBusy ? 'Loading...' : 'Preview All Files'}
              </button>
            </div>
          </div>
        </div>
      )}

      {settingsScopeIsGeneral && (
        <AppDetailAutomationSettings
          apiClient={apiClient}
          appId={appId}
          app={app}
          selectedContainer={selectedContainer}
          hasContainers={hasContainers}
          hookUrl={hookUrl}
          hookStatus={hookStatus}
          hookInstalled={hookInstalled}
          hookDisabled={hookDisabled}
          hookIncomplete={hookIncomplete}
          hookNeedsRecreate={hookNeedsRecreate}
          hookBusy={hookBusy}
          autofixEnabled={autofixEnabled}
          autofixFeatureBranches={autofixFeatureBranches}
          autofixPrompt={autofixPrompt}
          autofixResult={autofixResult}
          appAutopilotMode={appAutopilotMode}
          appEnablePushReminder={appEnablePushReminder}
          appTemplateBuildFailed={appTemplateBuildFailed}
          appTemplateMergeConflict={appTemplateMergeConflict}
          appTemplateReadySemi={appTemplateReadySemi}
          appTemplateReadyFull={appTemplateReadyFull}
          appAutopilotResult={appAutopilotResult}
          autoMrFeedback={autoMrFeedback}
          mrFeedbackResult={mrFeedbackResult}
          restartResult={restartResult}
          backgroundJobResult={backgroundJobResult}
          onSetAutofixEnabled={onSetAutofixEnabled}
          onSetAutofixFeatureBranches={onSetAutofixFeatureBranches}
          onSetAutofixPrompt={onSetAutofixPrompt}
          onSaveAutofix={onSaveAutofix}
          onResetAutofixTemplate={onResetAutofixTemplate}
          onSetAppAutopilotMode={onSetAppAutopilotMode}
          onSetAppEnablePushReminder={onSetAppEnablePushReminder}
          onSetAppTemplateBuildFailed={onSetAppTemplateBuildFailed}
          onSetAppTemplateMergeConflict={onSetAppTemplateMergeConflict}
          onSetAppTemplateReadySemi={onSetAppTemplateReadySemi}
          onSetAppTemplateReadyFull={onSetAppTemplateReadyFull}
          onSaveAppAutopilot={onSaveAppAutopilot}
          onSetAutoMrFeedback={onSetAutoMrFeedback}
          onSaveMrFeedback={onSaveMrFeedback}
          onAddGitlabHook={onAddGitlabHook}
          onRemoveGitlabHook={onRemoveGitlabHook}
          onSetSelectedContainer={onSetSelectedContainer}
          onRestartContainers={onRestartContainers}
          onSetBackgroundJobResult={onSetBackgroundJobResult}
          onSetError={onSetError}
        />
      )}
    </>
  );
}

function splitDnsRecordName(
  dnsRecordName: string | null | undefined,
  configuredDomains: string[]
): { domain: string; subdomain: string } {
  const record = (dnsRecordName ?? '').trim();
  if (!record) {
    return { domain: '', subdomain: '' };
  }
  const orderedDomains = configuredDomains
    .map((domain) => domain.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  for (const domain of orderedDomains) {
    if (record === domain) {
      return { domain, subdomain: '' };
    }
    if (record.endsWith(`.${domain}`)) {
      return {
        domain,
        subdomain: record.slice(0, -(domain.length + 1))
      };
    }
  }
  return { domain: '', subdomain: record };
}

// --- Identity Services Card ---

function IdentityServicesCard({
  identityServices,
  identityServicesBusy,
  identityResult,
  ymlIdConfigured,
  identityHealth,
  identityHealthBusy,
  onReconcileIdentity,
  onDeprovisionIdentity,
  onManualIdentitySecrets,
  onCheckIdentityHealth
}: {
  identityServices: IdentityServiceSummary[];
  identityServicesBusy: boolean;
  identityResult: string | null;
  ymlIdConfigured: boolean;
  identityHealth: IdentityHealthResponse | null;
  identityHealthBusy: boolean;
  onReconcileIdentity: (serviceName: string) => void | Promise<void>;
  onDeprovisionIdentity: (serviceName: string) => void | Promise<void>;
  onManualIdentitySecrets: (serviceName: string, clientId: string, clientSecret: string) => void | Promise<void>;
  onCheckIdentityHealth: () => void | Promise<void>;
}) {
  const [manualService, setManualService] = useState<string | null>(null);
  const [manualClientId, setManualClientId] = useState('');
  const [manualClientSecret, setManualClientSecret] = useState('');

  if (identityServices.length === 0 && ymlIdConfigured) {
    return null;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>Identity Services</h3>
        <div className="row">
          <button
            type="button"
            className="ghost compact"
            disabled={identityHealthBusy}
            onClick={() => void onCheckIdentityHealth()}
          >
            {identityHealthBusy ? 'Checking...' : 'Test Admin Connection'}
          </button>
          {identityHealth && (
            <IdentityHealthBadge health={identityHealth} />
          )}
        </div>
      </div>
      <div className="stack">
        <p className="status">
          Manage OAuth/OIDC client credentials for identity services defined in{' '}
          <span className="mono">deploy/ops.yaml</span>. Credentials are stored as encrypted stage
          secrets.
        </p>
        {identityResult && <p className="status">{identityResult}</p>}

        {identityServices.length === 0 ? (
          <p className="status">No identity services configured in the manifest.</p>
        ) : (
          <ul className="list">
            {identityServices.map((svc) => {
              const isProvisioned = svc.provisioned === true;
              const hasClientId = svc.has_client_id === true;
              const hasClientSecret = svc.has_client_secret === true;
              const isComplete = hasClientId && hasClientSecret;
              const showManual = manualService === svc.service_name;

              return (
                <li key={svc.service_name}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <div className="stack">
                      <div className="row">
                        <span className="mono">{svc.service_name}</span>
                        <span className="hint">({svc.service_type})</span>
                        {isComplete && <span className="hint" style={{ color: 'green' }}>Configured</span>}
                        {!isComplete && isProvisioned && <span className="hint" style={{ color: '#856404' }}>Partial</span>}
                        {!isProvisioned && <span className="hint" style={{ color: '#dc3545' }}>Not provisioned</span>}
                      </div>
                      <div className="status" style={{ fontSize: '0.85rem' }}>
                        <span>client_id: {hasClientId ? 'set' : 'missing'}</span>
                        <span style={{ marginLeft: '0.5rem' }}>client_secret: {hasClientSecret ? 'set' : 'missing'}</span>
                      </div>
                    </div>
                    <div className="row">
                          <button
                            type="button"
                            className="primary compact"
                            disabled={identityServicesBusy}
                            onClick={() => void onReconcileIdentity(svc.service_name)}
                          >
                            {identityServicesBusy ? 'Working...' : isProvisioned ? 'Re-reconcile' : 'Auto-Provision'}
                          </button>
                          {isProvisioned && (
                            <button
                              type="button"
                              className="ghost compact"
                              disabled={identityServicesBusy}
                              onClick={() => void onDeprovisionIdentity(svc.service_name)}
                            >
                              Deprovision
                            </button>
                          )}
                      <button
                        type="button"
                        className="ghost compact"
                        onClick={() => {
                          setManualService(showManual ? null : svc.service_name);
                          setManualClientId('');
                          setManualClientSecret('');
                        }}
                      >
                        {showManual ? 'Cancel' : 'Enter manually'}
                      </button>
                    </div>
                  </div>

                  {showManual && (
                    <div className="stack" style={{ marginTop: '0.5rem', marginLeft: '1rem' }}>
                      <p className="hint">
                        Enter existing client credentials manually. This stores them as stage secrets
                        without contacting yml-id.
                      </p>
                      <label className="row">
                        <span style={{ minWidth: '100px' }}>Client ID:</span>
                        <input
                          type="text"
                          value={manualClientId}
                          onChange={(e) => setManualClientId(e.target.value)}
                          placeholder="e.g., myapp-app"
                          autoComplete="off"
                        />
                      </label>
                      <label className="row">
                        <span style={{ minWidth: '100px' }}>Client Secret:</span>
                        <input
                          type="password"
                          value={manualClientSecret}
                          onChange={(e) => setManualClientSecret(e.target.value)}
                          placeholder="Enter client secret"
                          autoComplete="new-password"
                        />
                      </label>
                      <div className="row">
                        <button
                          type="button"
                          disabled={!manualClientId.trim() || !manualClientSecret.trim() || identityServicesBusy}
                          onClick={() => {
                            void onManualIdentitySecrets(svc.service_name, manualClientId.trim(), manualClientSecret.trim());
                            setManualService(null);
                            setManualClientId('');
                            setManualClientSecret('');
                          }}
                        >
                          Save credentials
                        </button>
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => {
                            setManualService(null);
                            setManualClientId('');
                            setManualClientSecret('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function IdentityHealthBadge({ health }: { health: IdentityHealthResponse }) {
  if (!health.configured) {
    return (
      <div className="stack" style={{ fontSize: '0.85rem' }}>
        <span className="hint" style={{ color: '#dc3545' }}>yml-id not configured</span>
        {health.error && <span className="hint">{health.error}</span>}
      </div>
    );
  }

  if (health.reachable === false) {
    return (
      <div className="stack" style={{ fontSize: '0.85rem' }}>
        <span style={{ color: '#dc3545' }}>Unreachable</span>
        {health.error && <span className="hint">{health.error}</span>}
        {health.base_url && (
          <span className="hint mono" style={{ fontSize: '0.75rem' }}>{health.base_url}</span>
        )}
      </div>
    );
  }

  if (health.auth_ok === false) {
    return (
      <div className="stack" style={{ fontSize: '0.85rem' }}>
        <span style={{ color: '#856404' }}>Auth failed</span>
        {health.error && <span className="hint">{health.error}</span>}
        <span className="hint mono" style={{ fontSize: '0.75rem' }}>
          {health.token_url || health.base_url}
        </span>
      </div>
    );
  }

  if (health.auth_ok === true) {
    return (
      <div className="stack" style={{ fontSize: '0.85rem' }}>
        <span style={{ color: 'green' }}>Admin OK</span>
        <span className="hint mono" style={{ fontSize: '0.75rem' }}>
          {health.issuer || health.base_url}
        </span>
      </div>
    );
  }

  return null;
}
